import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ArrowPathIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import BackButton from '../ui/BackButton'

export interface ColumnGroupDef {
  group: string
  columns: { key: string; label: string }[]
}

interface PassAnalyticsColumnSelectorDialogProps {
  open: boolean
  onClose: () => void
  groups: ColumnGroupDef[]
  /** Currently visible column keys. */
  visibleKeys: string[]
  /** Default (factory) visible column keys, used by the Reset button. */
  defaultKeys: string[]
  onApply: (keys: string[]) => void
}

const MIN_REQUIRED = 2

/**
 * Toggle which columns the Pass Analytics table renders. Mirrors the
 * Welding Parameters `ColumnSelectorDialog` layout (back-arrow header,
 * search, select-all / reset, blue counter banner, white category cards
 * with red underline title, Save / Cancel footer) so the two modals feel
 * consistent. Pass Analytics columns are flat (no Min/Avg/Max children),
 * so each group renders a simple checkbox grid.
 */
export default function PassAnalyticsColumnSelectorDialog({
  open,
  onClose,
  groups,
  visibleKeys,
  defaultKeys,
  onApply
}: PassAnalyticsColumnSelectorDialogProps) {
  const allKeys = useMemo(() => groups.flatMap((g) => g.columns.map((c) => c.key)), [groups])
  const [draft, setDraft] = useState<Set<string>>(() => new Set(visibleKeys))
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    if (open) {
      setDraft(new Set(visibleKeys))
      setSearchQuery('')
    }
  }, [open, visibleKeys])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const visibleGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return groups
    return groups
      .map((g) => ({
        ...g,
        columns: g.columns.filter(
          (c) => c.label.toLowerCase().includes(q) || c.key.toLowerCase().includes(q)
        )
      }))
      .filter((g) => g.columns.length > 0)
  }, [groups, searchQuery])

  const filteredKeys = useMemo(
    () => visibleGroups.flatMap((g) => g.columns.map((c) => c.key)),
    [visibleGroups]
  )

  const filteredSelectedCount = useMemo(
    () => filteredKeys.filter((k) => draft.has(k)).length,
    [filteredKeys, draft]
  )
  const filteredAllSelected =
    filteredKeys.length > 0 && filteredSelectedCount === filteredKeys.length

  const selectedCount = draft.size
  const totalCount = allKeys.length
  const canSave = selectedCount >= MIN_REQUIRED

  if (!open) return null

  const toggle = (key: string) => {
    setDraft((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleSelectAll = () => {
    setDraft((prev) => {
      const next = new Set(prev)
      if (filteredAllSelected) {
        for (const k of filteredKeys) next.delete(k)
      } else {
        for (const k of filteredKeys) next.add(k)
      }
      return next
    })
  }

  const handleReset = () => {
    setDraft(new Set(defaultKeys))
  }

  const handleSave = () => {
    if (!canSave) return
    // Preserve canonical column order; the table also filters by membership,
    // but a stable ordering helps debugging and tests.
    const ordered = allKeys.filter((k) => draft.has(k))
    onApply(ordered)
  }

  const content = (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pa-column-selector-title"
      data-testid="pa-column-selector"
    >
      <div className="relative flex h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
        {/* Header */}
        <div className="flex flex-shrink-0 items-center gap-3 border-b border-[#E2E5EA] px-5 py-4">
          <BackButton onClick={onClose} tooltip="Close" />
          <h2
            id="pa-column-selector-title"
            className="font-heading text-base font-semibold text-blueprint-blue"
          >
            Select Columns
          </h2>
        </div>

        {/* Body */}
        <div className="flex flex-1 min-h-0 flex-col gap-3 px-5 py-4">
          {/* Search + controls */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                data-testid="pa-column-selector-search"
                placeholder="Search columns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-md border border-[#E2E5EA] bg-white py-2 pl-9 pr-3 text-sm text-text-grey placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <div className="flex flex-shrink-0 gap-2">
              <button
                type="button"
                data-testid="pa-column-selector-select-all"
                onClick={handleSelectAll}
                className="inline-flex items-center gap-1.5 rounded-md border border-[#E2E5EA] bg-white px-3 py-1.5 text-xs font-semibold text-heritage-grey shadow-sm transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
              >
                {filteredAllSelected ? 'Deselect All' : 'Select All'}
              </button>
              <button
                type="button"
                data-testid="pa-column-selector-reset"
                onClick={handleReset}
                className="inline-flex items-center gap-1.5 rounded-md border border-[#E2E5EA] bg-white px-3 py-1.5 text-xs font-semibold text-heritage-grey shadow-sm transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
              >
                <ArrowPathIcon className="h-3.5 w-3.5" />
                Reset to default
              </button>
            </div>
          </div>

          {/* Selected count */}
          <div className="flex flex-shrink-0 items-center gap-2 rounded-md border border-blue-100 bg-blue-50/70 px-3 py-2 text-xs text-text-grey">
            <span className="font-semibold" data-testid="pa-column-selector-count">
              {selectedCount} of {totalCount} columns selected
            </span>
            {selectedCount < MIN_REQUIRED ? (
              <span className="font-semibold text-error-600">
                (Minimum {MIN_REQUIRED} columns required)
              </span>
            ) : null}
          </div>

          {/* Categories list */}
          <div className="flex-1 min-h-0 overflow-y-auto rounded-md border border-[#E2E5EA] bg-page-bg p-4">
            {visibleGroups.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-center text-sm text-heritage-grey">
                <p className="font-medium">No columns match &ldquo;{searchQuery}&rdquo;</p>
                <p className="mt-1 text-xs">Try a different search term.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {visibleGroups.map((g) => (
                  <CategoryCard
                    key={g.group}
                    title={g.group}
                    testId={`pa-column-group-${g.group}`}
                  >
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      {g.columns.map((c) => {
                        const checked = draft.has(c.key)
                        const id = `pa-col-cb-${c.key}`
                        return (
                          <label
                            key={c.key}
                            data-testid={`pa-column-toggle-${c.key}`}
                            htmlFor={id}
                            className={clsx(
                              'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 transition-all',
                              checked
                                ? 'border-primary-200 bg-primary-50/40 shadow-sm'
                                : 'border-[#E2E5EA] bg-white hover:border-[#C9CDD4]'
                            )}
                          >
                            <input
                              type="checkbox"
                              id={id}
                              checked={checked}
                              onChange={() => toggle(c.key)}
                              className="h-4 w-4 flex-shrink-0 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
                              aria-label={c.label}
                            />
                            <span className="flex-1 select-none text-sm font-medium text-text-grey">
                              {c.label}
                            </span>
                          </label>
                        )
                      })}
                    </div>
                  </CategoryCard>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-shrink-0 items-center justify-end gap-3 border-t border-[#E2E5EA] bg-white px-5 py-3">
          <button
            type="button"
            data-testid="pa-column-selector-cancel"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="pa-column-selector-apply"
            onClick={handleSave}
            disabled={!canSave}
            className="inline-flex items-center justify-center rounded-md bg-hero-red px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-hero-red-60 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}

interface CategoryCardProps {
  title: string
  testId?: string
  children: React.ReactNode
}

function CategoryCard({ title, testId, children }: CategoryCardProps) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col rounded-lg border border-[#E2E5EA] bg-white p-4 shadow-sm"
    >
      <h3 className="mb-3 flex-shrink-0 border-b-2 border-hero-red pb-2 text-sm font-bold uppercase tracking-wide text-blueprint-blue">
        {title}
      </h3>
      <div className="flex-1">{children}</div>
    </div>
  )
}
